import moment from 'moment'
import {
  CommercialNetwork,
  InsuredObjectGroup,
  RecalculatePremiumClientRequest,
} from '../../../@types/RecalculatePremium'
import { ENV } from '../../../Environment'
import { usePaymentStore } from '../../../Stores/paymentStore'
import { useSimulatorStore } from '../../../Stores/simulatorStore'
import { useFillDataForSimulationStore } from '../../../Stores/useFillDataForSimulationStore'
import { useRunPetisSimulationStore } from '../../../Stores/useRunPetisSimulationStore'

export const useRecalculatePremiumPayload = () => {
  const { initialDate, entitySelected, typeOfPrice, columnSelected } =
    useSimulatorStore()
  const { runPetisSimulation, selectedPackCampaign } =
    useRunPetisSimulationStore()
  const { fillDataForSimulationRequest } = useFillDataForSimulationStore()
  const { directDebit } = usePaymentStore()

  const runPetis = runPetisSimulation?.runPetisSimulationResponse
  const insured = runPetis?.insuredObjectGroup
  const campaigns = runPetisSimulation?.discountsCampaigns?.filter(
    (campaign) => campaign.campaignId === selectedPackCampaign?.campaignId,
  )

  const insuredObjectGroup: InsuredObjectGroup = {
    insuredObjectGroupId: insured?.insuredObjectGroupId || '1',
    insuredObjectGroupCode: insured?.insuredObjectGroupCode || '',
    insuredObjectGroupSequence: insured?.insuredObjectGroupSequence as never,
    insuredObjects: insured?.insuredObjects?.map((item) => ({
      insuredObjectId: item?.insuredObjectId || '',
      insuredObjectCode: item?.insuredObjectCode || '',
      insuredObjectSequence: item?.insuredObjectSequence,
      insuredPet: {
        animalName: item?.insuredPet?.animalName || '',
        animalBirthDate: item?.insuredPet?.animalBirthDate || '',
        animalSpecies: item?.insuredPet?.animalSpecies || '',
        animalSpeciesCode: item?.insuredPet?.animalSpeciesCode || '',
        animalBreedCode: item?.insuredPet?.animalBreedCode || '',
        animalBreed: item?.insuredPet?.animalBreed || '',
      },
      questionnaire: {
        startDate: item?.questionnaire?.startDate || '',
        endDate: item?.questionnaire?.endDate || '',
        id: item?.questionnaire?.id || '',
        statusCode: item?.questionnaire?.statusCode || '',
        sections: item?.questionnaire?.sections?.map((section) => ({
          title: section?.title || '',
          code: section?.code || '',
          questions: section?.questions?.map((question) => ({
            questionnaireId: question?.id || '',
            id: question?.id || '',
            questionPhrase: question?.questionPhrase || '',
            order: Number(question?.order) || 0,
            conditions: question.conditions || [],
            type: question?.answers?.[0]?.responseTypeCode || '',
            answers: question?.answers?.map((answer) => ({
              id: answer?.id || '',
              answerPhrase: answer?.answerPhrase || '',
              date: answer?.date || '',
              responseTypeCode: answer?.responseTypeCode || '',
            })),
          })),
        })),
      },
      packs: item?.packsObject?.packs?.map((pack) => {
        const allCoverages = [
          ...(item?.coveragesObject?.mandatoryCoverages || []),
          ...(item?.coveragesObject?.optionalCoverages || []),
        ]

        return {
          packId: pack?.packId || '',
          packName: pack?.packName || '',
          packDescription: pack?.packDescription || '',
          packOrder: pack?.packOrder || 0,
          recommended: pack?.recommended || false,
          coverages: pack?.coverages?.map((coverage) => ({
            asfBranchCode: coverage?.asfBranchCode,
            accountingId: coverage?.accountingId || '',
            accountingVariableCode: coverage?.accountingVariableCode || '',
            accountingValue: coverage?.accountingValue || 0,
            referencePeriodVariableCode:
              coverage?.referencePeriodVariableCode || '',
            referencePeriodValue: coverage?.referencePeriodValue || 0,
            coverageId: coverage?.coverageId || '',
            riskGroupCode: coverage?.riskGroupCode || '',
            coverageName:
              allCoverages.find((c) => c?.coverageId === coverage?.coverageId)
                ?.coverageName || '',
            isSelected: coverage?.isPreSelected,
            isPreSelected: coverage?.isPreSelected,
            mandatory: coverage?.mandatory,
            sumInsured: {
              sumInsuredTypeCode:
                coverage?.sumInsured?.sumInsuredTypeCode || '',
              minValue: coverage?.sumInsured?.minValue || 0,
              maxValue: coverage?.sumInsured?.maxValue || 0,
              sumInsuredFixedValue:
                coverage?.sumInsured?.sumInsuredFixedValue || 0,
              sumInsuredValue:
                Number(coverage?.sumInsured?.sumInsuredValue) || 0,
              relatedMinPercent: coverage?.sumInsured?.relatedMinPercent || 0,
              relatedMaxPercent: coverage?.sumInsured?.relatedMaxPercent || 0,
              capitalTiers: coverage?.sumInsured?.capitalTiers,
            },
            coveragesValues: {
              coverageTaxValues: coverage?.coveragesValues
                ?.coverageTaxValues as never,
            },
            gracePeriod: {
              gracePeriodTypeId: coverage?.gracePeriod?.typeId || '',
            },
            excessDeductible: {
              excessTypeCode: coverage?.excessDeductible?.excessTypeCode || '',
              excessMinValue: coverage?.excessDeductible?.excessMinValue || 0,
              excessMaxValue: coverage?.excessDeductible?.excessMaxValue || 0,
              excessValue:
                Number(coverage?.excessDeductible?.selectedValue) || 0,
              excessPercent: coverage?.excessDeductible?.excessMaxPercent || 0,
              excessMinPercent:
                coverage?.excessDeductible?.excessMinPercent || 0,
              excessMaxPercent:
                coverage?.excessDeductible?.excessMaxPercent || 0,
              excessTemporaryValues:
                coverage?.excessDeductible?.excessTemporaryValues || [],
            },
            riskFactors: (coverage?.riskFactors as never) || [],
          })),
        }
      }),
    })),
  }

  const handleRecalculatePremiumPayload = () => {
    const campaignsDirectDebit = selectedPackCampaign?.collectionMethod === 'DD'

    const payload: RecalculatePremiumClientRequest & {
      isDirectDebit: boolean
    } = {
      discountsCampaigns:
        selectedPackCampaign?.id &&
        campaignsDirectDebit === Boolean(Number(directDebit))
          ? campaigns?.map((campaign) => ({
              ...campaign,
              isSelected: true,
              coverages: campaign?.coverages?.map((coverage) => ({
                ...coverage,
                value: coverage.maxDiscount,
              })),
            }))
          : (null as never),
      agentDetailsRequest: fillDataForSimulationRequest?.agentDetailsResponse,
      isDirectDebit: Boolean(Number(directDebit)),
      quoteId: runPetis?.quoteId || '',
      datesAndInstallment: {
        startDate: moment(initialDate, 'DD-MM-YYYY').format('YYYY-MM-DD') || '',
      },
      selectedPackId: columnSelected || '',
      selectedInstallmentPlan: typeOfPrice || '',
      productData: {
        commercialNetwork: runPetis?.productData
          ?.commercialNetwork as CommercialNetwork[],
        segment: {
          segmentCode: runPetis?.productData?.segment?.segmentCode || '',
        },
        lineofBusiness: {
          countryID: runPetis?.productData?.lineofBusiness?.countryID || '',
          lineofBusinessID:
            runPetis?.productData?.lineofBusiness?.lineofBusinessID || '',
          productTypeID:
            runPetis?.productData?.lineofBusiness?.productTypeID || '',
          productFamilyID:
            runPetis?.productData?.lineofBusiness?.productFamilyID || '',
        },
        company: runPetis?.productData?.company,
        brand: runPetis?.productData?.brand,
        distributionChannel: runPetis?.productData?.distributionChannel,
        productCode: runPetis?.productData?.productCode,
      },
      policyHolder: {
        entityId: entitySelected?.idEntity || '',
        birthDate: runPetis?.policyHolder?.birthDate,
        idExternalType: runPetis?.policyHolder?.idExternalType || '',
      },
      insuredObjectGroup,
      coreId: ENV().CoreSystem,
      operationType: 'E', //TODO: verificar este dado, onde pegar esse valor?
    }

    return payload
  }

  return {
    recalculatePremiumPayload: handleRecalculatePremiumPayload(),
  }
}
